import React, { useEffect, useState, useCallback } from 'react';
import { StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { useHistory, useRouteMatch } from 'src/router';
import { DNABox, DNAText, DNAIcon, DNAButton, DNACard, DNASpinner } from '@alucio/lux-ui';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import ROUTES from 'src/router/routeDef';
import { buildSearchRoute } from 'src/utils/searchRouteText';

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    zIndex: 9999,
  },
  card: {
    padding: 12,
    minWidth: 260,
    maxWidth: 320,
  },
  headerButton: {
    padding: 4,
    marginLeft: 4,
  },
});

const AISearchFloatingProgress: React.FC = () => {
  const { aiSearchSessionData } = useDocumentSearchV2Context();
  const { loading, statusMessage, searchText, report, sessionId } = aiSearchSessionData;
  const history = useHistory();
  const [fadeAnim] = useState(() => new Animated.Value(0));
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const isSearchResultsPage = Boolean(useRouteMatch(ROUTES.SEARCH_RESULTS.PATH));
  const isVisible = (loading || !!report) && !isSearchResultsPage && !isDismissed;
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    }

    const animation = Animated.timing(fadeAnim, {
      toValue: isVisible ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    });

    animation.start(({ finished }) => {
      if (finished && !isVisible) {
        setShouldRender(false);
      }
    });

    return () => {
      animation.stop();
    };
  }, [isVisible, fadeAnim]);

  // Reset dismissal when a new search starts
  useEffect(() => {
    if (sessionId) {
      setIsDismissed(false);
    }
  }, [sessionId]);

  const handleNavigate = useCallback(() => {
    history.push(buildSearchRoute(searchText));

    if (!loading) {
      setIsDismissed(true);
    }
  }, [history, searchText, loading]);

  const handleClose = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized(prev => !prev);
  }, []);

  // Keep it mounted if visible or animating out
  if (!shouldRender) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [
            {
              translateY: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [20, 0],
              }),
            },
          ],
        },
      ]}
    >
      <DNACard appearance="float" style={styles.card}>
        <DNABox appearance="col" spacing="sm">
          <DNABox appearance="row" alignY="center" spacing="between">
            <DNABox appearance="row" alignY="center" spacing="sm">
              {loading ? (
                <DNASpinner size="small" status="primary" />
              ) : (
                <DNAIcon.Styled name="check-circle-fill" size="lg" status="success" />
              )}
              <DNAText b1 bold status="primary" numberOfLines={1}>
                {loading ? 'AI Summarizing...' : 'AI Summary Ready'}
              </DNAText>
            </DNABox>
            <DNABox appearance="row" alignY="center">
              <TouchableOpacity onPress={toggleMinimize} style={styles.headerButton}>
                <DNAIcon.Styled name={isMinimized ? 'plus' : 'minus'} size="sm" status="subtle" />
              </TouchableOpacity>
              <TouchableOpacity onPress={handleClose} style={styles.headerButton}>
                <DNAIcon.Styled name="x" size="sm" status="subtle" />
              </TouchableOpacity>
            </DNABox>
          </DNABox>

          {!isMinimized && (
            <DNABox appearance="col" spacing="md" alignX="stretch">
              <DNABox appearance="col" spacing="xs">
                <DNAText b2 status="tertiary" numberOfLines={1} italic>
                  {searchText}
                </DNAText>
                {loading && (
                  <DNAText c1 status="subtle" numberOfLines={1}>
                    {statusMessage || 'Processing documents...'}
                  </DNAText>
                )}
              </DNABox>
              {!loading && (
                <DNAButton
                  appearance="filled"
                  status="primary"
                  size="sm"
                  stretch
                  onPress={handleNavigate}
                >
                  View Results
                </DNAButton>
              )}
              {loading && (
                <DNAButton
                  appearance="filled"
                  status="primary"
                  size="sm"
                  stretch
                  onPress={handleNavigate}
                >
                  Follow progress
                </DNAButton>
              )}
            </DNABox>
          )}

          {isMinimized && (
            <TouchableOpacity onPress={toggleMinimize}>
              <DNAText b2 status="tertiary" numberOfLines={1} italic>
                {searchText}
              </DNAText>
            </TouchableOpacity>
          )}
        </DNABox>
      </DNACard>
    </Animated.View>
  );
};

export default AISearchFloatingProgress;
